import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {actions} from '../../store';
import {useTranslation} from 'react-i18next';
import {PaginationMode} from '../../components/DataGrid/types';
import DataGrid from '../../components/DataGrid/DataGrid';
import {generateColumns, useStyles} from './AccountPhoneBookList.utils';
import {APIErrorInterface, ReduxState} from '../../store/types';
import { PhonebookRecord } from '../../store/actions/accountPhoneBook/payloads';
import CustomizedButton from '../../components/Button/Button';
import { Add } from '@material-ui/icons';
import { NewAccountPhoneBookContactDialogProps } from './NewAccountPhoneBookContactDialog.utils';
import NewAccountPhoneBookEntryDialog from './NewAccountPhoneBookContactDialog';
import AlertDialog from '../../components/AlertDialog/AlertDialog';
import { DialogButton } from '../../components/AlertDialog/DialogContainer';
import { showToast } from '../../utils/showToast';
import { YesNo } from '../../store/types/CallScreening';
import { ServiceFeatureName } from '../../store/types/ServiceFeature';
import InfoBanner from '../../components/InfoBanner/InfoBanner';

export type AccountPhoneBookListProps = {
}

export const AccountPhoneBookList: React.VFC<AccountPhoneBookListProps> = ({
    }) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const [isRemoveModalOpen, setIsRemoveModalOpen] = useState<PhonebookRecord | undefined>(undefined);

    const [showAddNewAccountPhoneBookContactDialog, setShowAddNewAccountPhoneBookContactDialog] 
        = useState<NewAccountPhoneBookContactDialogProps>({
        isOpen: false,
    });

    const {list, isLoading, total} = useSelector(
        (state: ReduxState) => state.accountPhoneBook,
    );

    const {isLoadingAccountDetails, accountDetails} = useSelector(
        (state: ReduxState) => state.accountControlPanel,
    );

    const [pagination, setPagination] = useState({page: 0, pageSize: 10});
    const [forceFirstPage, setForceFirstPage] = useState(false);

    const fetchData = useCallback((page: number, pageSize: number) => {
        dispatch(actions.getAccountPhoneBookList.request({
            offset: page * pageSize,
            limit: pageSize,
            get_total: 1
        }));
    }, []);

    useEffect(() => {
        fetchData(pagination.page, pagination.pageSize);
    }, []);

    const addEditObject = useCallback((editItem?: PhonebookRecord) => {
        setShowAddNewAccountPhoneBookContactDialog({
            isOpen: true,
            clickOnClose: (reload: boolean) => {
                setShowAddNewAccountPhoneBookContactDialog({
                    isOpen: false,
                    editRecord: editItem
                });
                if(reload) {
                    fetchData(pagination.page, pagination.pageSize);
                }
            },
            editRecord: editItem
        });
    }, [setShowAddNewAccountPhoneBookContactDialog, fetchData, pagination]);

    const deleteContact = useCallback((item: PhonebookRecord) => {
        const isLastOnPage = pagination.page > 0 && list?.length === 1;
        dispatch(actions.deleteAccountPhoneBookRecord.request({
            i_account_phonebook: item.i_account_phonebook,
            callback: (error?: APIErrorInterface) => {
                if(!error)
                {
                    if(isLastOnPage)
                    {
                        setForceFirstPage(true);
                        setPagination({...pagination, page: 0});
                        fetchData(0, pagination.pageSize);
                    }
                    else
                    {
                        fetchData(pagination.page, pagination.pageSize);
                    }
                }
                else
                {
                    showToast(error.faultstring);
                }
                setIsRemoveModalOpen(undefined);
            }
        }));
    }, [fetchData, setIsRemoveModalOpen, pagination, list]);

    const setAccountPhoneBookRecordFavorit = useCallback((checked: boolean, record: PhonebookRecord) => {
        dispatch(actions.setAccountPhoneBookRecordFavorit.request({
            i_account_phonebook: record.i_account_phonebook,
            value: checked ? YesNo.Yes : YesNo.No,
            callback: (error?: APIErrorInterface) => {
                if(error)
                {
                    switch(error.faultcode)
                    {
                        case 'Server.Account.max_favorites_exceeded':
                            const match3 = error.faultstring?.match(/\((\d+)\)/);
                            const value3 = match3 ? Number(match3[1]) : 0;
                            showToast(t('errors:accountPhoneBook.favoriteNumberLimit', {
                                max_favorites: value3
                            }));
                            break;
                        default:
                            showToast(error.faultstring);
                            break;
                    } 
                }
                setIsRemoveModalOpen(undefined);
            }
        }));
    }, []);

    const serviceFeatureEnabled = useMemo(() => {
        return accountDetails?.service_features?.find(e => e.name == ServiceFeatureName.Phonebook)?.effective_flag_value == YesNo.Yes;
    }, [accountDetails]);

    const columns = useMemo(() => {
        return generateColumns(
            t,
            classes,
            setAccountPhoneBookRecordFavorit,
            addEditObject,
            addEditObject,
            (itm: PhonebookRecord) => {
                setIsRemoveModalOpen(itm);
            },
        );
    }, [setShowAddNewAccountPhoneBookContactDialog, setIsRemoveModalOpen, addEditObject]);

    if(accountDetails?.service_features && !serviceFeatureEnabled) {
        return (
            <InfoBanner
                text={`${t('screens:accountPhoneBook.phoneBookDisabled')}. ${t('screens:accountPhoneBook.phoneBookDisabledDescription')}`}
            />
        );
    }

    return isLoadingAccountDetails ? (<></>) : (
        <>
            <div className={classes.inputsContainer}>
                <CustomizedButton
                    dataQa={'add-contact-button'}
                    primary
                    accent
                    onClick={() => {
                        addEditObject();
                    }}
                    className={classes.button}
                >
                    <Add className={classes.addIcon} />
                    {t('screens:accountPhoneBook.addContact')}
                </CustomizedButton>
            </div>
            <DataGrid<PhonebookRecord>
                columns={columns}
                data={list ?? []}
                rowCount={total ?? 0}
                loading={isLoading || isLoadingAccountDetails}
                onPageChange={(v) => {
                    setForceFirstPage(false);
                    setPagination({page: v.page, pageSize: v.pageSize});
                    fetchData(v.page, v.pageSize);
                }}
                onPageSizeChange={(v) => {
                    // DataGrid keeps the first visible row when the page size changes
                    const page = Math.floor(
                        (pagination.page * pagination.pageSize) / v.pageSize,
                    );
                    setForceFirstPage(false);
                    setPagination({page, pageSize: v.pageSize});
                    fetchData(page, v.pageSize);
                }}
                paginationMode={PaginationMode.Server}
                forceFirstPage={forceFirstPage}
                centeredRows
                narrowRows
                customRowHeight={56}
                initialPageSize={pagination.pageSize}
                classes={{
                    tableContainer: classes.tableContainer,
                }}
                getItemId={(item) => item.i_account_phonebook}
            />
            
            <NewAccountPhoneBookEntryDialog {...showAddNewAccountPhoneBookContactDialog} />
            
            <AlertDialog
                isOpen={isRemoveModalOpen != undefined}
                dataQa="remove-call-history"
                hideHeader={true}
                className={classes.container}
                description={
                    <span style={{ whiteSpace: 'pre-wrap' }}>
                        {t('screens:accountPhoneBook.deleteContact', {
                            value: isRemoveModalOpen?.phone_number
                        })}
                    </span>
                }
                dialogActionsButtons={[
                    <DialogButton
                        key="cancel"
                        label={t('common:cancel')}
                        onClick={() => setIsRemoveModalOpen(undefined)}
                    />,
                    <DialogButton
                        key="delete"
                        label={t('common:delete')}
                        className={classes.primaryModalButton}
                        onClick={() => {
                            if(isRemoveModalOpen)
                                deleteContact(isRemoveModalOpen);
                        }}
                    />,
                ]}
            />
        </>
    );
};
